import { LineType, PolygonType, getSupportedIndicators, registerIndicator } from 'klinecharts'
import type { Chart, IndicatorCreate, IndicatorStyle, IndicatorTemplate, SmoothLineStyle } from 'klinecharts'
import {
  DEFAULT_LINE_COLORS,
  colorSlotsFor,
  defaultColors,
  defaultInputs,
  engineBase,
  fromCalcParams,
  registerClone,
  resolveDef,
  styleSlotsFor,
  toCalcParams,
} from '@/indicators'
import type { IndicatorDef } from '@/indicators'
import { EMPTY_TOOLTIP } from './chartStyles'
import { getIndicator, asOverlayId } from './types'
import type { IndicatorInstance, IndicatorSettings } from './types'
import { TAIL_SAFE_INDICATORS, unwrapCalc, withTailCalc } from './tailIndicatorCalc'

/**
 * Indicator create/update path shared by the picker, the settings dialog and the
 * legends, so they can never diverge. Main-pane indicators overlay the candles
 * ('candle_pane'); the rest open their own sub-pane.
 *
 * An instance is a def (`id` + `version`, see src/indicators) plus settings.
 * Version 1 of a pre-library id is the klinecharts built-in template, untouched;
 * every other version is ours, registered into the engine by the library adapter
 * before the synchronous work here runs (ensureRegistered / preloadIndicators).
 *
 * Multiple instances of the same def are supported. klinecharts addresses an
 * instance ONLY by (paneId, name) and rejects a same-name create on one pane,
 * so each instance gets a unique ENGINE name: the def's engine base ('RSI' for
 * the builtin, 'RSI@2' for ours) for the first, a registered clone ("RSI@2#2",
 * …) for duplicates — see mintEngineName / ensureIndicatorClone. The def id +
 * version stay the display + persistence identity. Volume is special chrome
 * (faded bars on the price pane — see volumeOverlay.ts), not a normal instance.
 */

export { DEFAULT_LINE_COLORS }

export function defaultSettings(def: IndicatorDef): IndicatorSettings {
  return {
    inputs: def.builtin ? {} : defaultInputs(def), // builtin: seeded from the engine's defaults after creation
    colors: defaultColors(def),
    thickness: 1,
    visible: true,
    lastValueMark: true, // "Labels on price scale"
    valuesInLegend: true, // "Values in status line"
  }
}

/** The def an instance (or a saved entry) pins. */
export const defOf = (inst: Pick<IndicatorInstance, 'name' | 'version'>): IndicatorDef | undefined =>
  resolveDef(inst.name, inst.version)

/** Reference-line chrome (RSI 70/50/30 …): locked dashed horizontals on the indicator's pane. */
const BAND_COLOR = 'rgba(136,136,136,0.45)'

/** Smallest free engine name for `base` given the names already held. */
export function mintEngineName(base: string, taken: Iterable<string>): string {
  const set = new Set(taken)
  if (!set.has(base)) return base
  for (let k = 2; ; k++) if (!set.has(`${base}#${k}`)) return `${base}#${k}`
}

/**
 * Make `cloneName` a registered copy of the def's template. Ours: from the
 * library (its calc chunk is already loaded — the add/restore paths await
 * ensureRegistered first). Builtin v1: klinecharts does not export its
 * templates, but their calc/regenerateFigures are `this`-independent and
 * readable off a live sibling instance. No-op when already registered — the
 * registry is module-global and survives chart disposal.
 */
export function ensureIndicatorClone(
  chart: Chart,
  def: IndicatorDef,
  cloneName: string,
  sibling?: { paneId: string; engineName: string },
): boolean {
  if (getSupportedIndicators().includes(cloneName)) return true
  if (!def.builtin) return registerClone(def, cloneName)
  if (!sibling) return false
  const live = getIndicator(chart, sibling.paneId, sibling.engineName)
  if (!live) return false
  registerIndicator({
    name: cloneName,
    shortName: def.id,
    series: live.series,
    calcParams: live.calcParams,
    precision: live.precision,
    shouldOhlc: live.shouldOhlc,
    shouldFormatBigNumber: live.shouldFormatBigNumber,
    minValue: live.minValue,
    maxValue: live.maxValue,
    figures: live.figures,
    // The engine's own calc, not the tail-only wrapper a live instance may carry: the clone's
    // instance is wrapped when it is created, like any other (applyIndicator).
    calc: unwrapCalc(live.calc),
    ...(live.regenerateFigures ? { regenerateFigures: live.regenerateFigures } : {}),
  } as IndicatorTemplate)
  return true
}

/**
 * The colour the legend paints each live FIGURE with (figure order = styleSlotsFor
 * order; a marker output contributes two figures): the figure's colour slot, a
 * histogram's "up" one. Builtin: by figure index.
 */
export function outputColors(def: IndicatorDef | undefined, s: IndicatorSettings): string[] {
  const colors = s.colors.length > 0 ? s.colors : DEFAULT_LINE_COLORS
  if (!def || def.builtin) return colors
  const slots = colorSlotsFor(def)
  return styleSlotsFor(def).map((ss, i) => {
    const role = ss.slot === 'bars' ? 'up' : ss.role
    const k = slots.findIndex((c) => c.outputKey === ss.outputKey && c.role === role)
    return colors[k >= 0 ? k : i] ?? colors[i % colors.length]
  })
}

type PolygonSlotStyle = IndicatorStyle['bars'][number]

const polygonStyle = (up: string, down: string): PolygonSlotStyle => ({
  style: PolygonType.Fill,
  borderSize: 1,
  borderStyle: LineType.Solid,
  borderDashedValue: [2, 2],
  upColor: up,
  downColor: down,
  noChangeColor: up,
})

/**
 * Apply per-instance styles. klinecharts indexes `styles.lines[k]`, `bars[k]`
 * and `circles[k]` per figure TYPE, so a def's colour slots are routed by
 * styleSlotsFor; a builtin keeps today's rule (line styles sliced to its live
 * figure count — passing more than it has figures makes the engine throw).
 */
export function styleIndicator(
  chart: Chart,
  def: IndicatorDef,
  name: string,
  paneId: string,
  s: IndicatorSettings,
): void {
  const colors = s.colors.length > 0 ? s.colors : DEFAULT_LINE_COLORS
  const size = s.thickness ?? 1
  // Full line-style objects — overrideIndicator replaces (not deep-merges) each
  // line, so a partial {color,size} would drop `dashedValue` and crash the
  // engine's line-draw merge.
  const lineStyle = (color: string): SmoothLineStyle => ({ style: LineType.Solid, smooth: false, size, color, dashedValue: [2, 2] })

  let lines: SmoothLineStyle[]
  const bars: PolygonSlotStyle[] = []
  const circles: PolygonSlotStyle[] = []
  if (def.builtin) {
    const n = getIndicator(chart, paneId, name)?.figures?.length || 1
    lines = colors.slice(0, n).map(lineStyle)
  } else {
    lines = []
    const slots = colorSlotsFor(def)
    const colorOf = (key: string, role: 'line' | 'dots' | 'up' | 'down', fallback: string) => {
      const k = slots.findIndex((c) => c.outputKey === key && c.role === role)
      return (k >= 0 ? colors[k] : undefined) ?? fallback
    }
    for (const ss of styleSlotsFor(def)) {
      const out = def.outputs.find((o) => o.key === ss.outputKey)!
      if (ss.slot === 'lines') lines[ss.index] = lineStyle(colorOf(ss.outputKey, 'line', out.color))
      else if (ss.slot === 'bars') bars[ss.index] = polygonStyle(colorOf(ss.outputKey, 'up', out.color), colorOf(ss.outputKey, 'down', out.downColor ?? out.color))
      else {
        // dots: one slot; marker: the bullish (up) and bearish (down) figures each take their own.
        const c = colorOf(ss.outputKey, ss.role, ss.role === 'down' ? (out.downColor ?? out.color) : out.color)
        circles[ss.index] = polygonStyle(c, c)
      }
    }
  }
  // "Labels on price scale" needs BOTH show + text.show (the engine gates on both).
  const showMark = s.lastValueMark !== false
  const markText = {
    show: showMark,
    ...(s.textColor ? { color: s.textColor } : {}),
  } as IndicatorStyle['lastValueMark']['text']
  chart.overrideIndicator(
    {
      name,
      styles: {
        lines,
        ...(bars.length > 0 ? { bars } : {}),
        ...(circles.length > 0 ? { circles } : {}),
        lastValueMark: { show: showMark, text: markText },
      },
    },
    paneId,
  )
}

export interface AppliedIndicator {
  paneId: string
  bandIds: string[]
  /** Settings with builtin `inputs` seeded from the engine defaults when empty. */
  settings: IndicatorSettings
}

/**
 * Create one indicator instance with the given settings. For a builtin the
 * calcParams are omitted at creation when the inputs are unset so the engine's
 * own defaults apply; the live values are then read back to seed the settings
 * dialog. `engineName` is the unique engine identity (the def's engine base or
 * a registered clone — see mintEngineName). A non-builtin def must already be
 * registered (ensureRegistered) or the engine returns null.
 */
export function applyIndicator(
  chart: Chart,
  def: IndicatorDef,
  settings?: IndicatorSettings,
  engineName: string = engineBase(def),
): AppliedIndicator | null {
  const s = settings ?? defaultSettings(def)
  const create: IndicatorCreate = {
    name: engineName,
    visible: s.visible !== false,
    createTooltipDataSource: EMPTY_TOOLTIP,
  }
  const calcParams = toCalcParams(def, s.inputs)
  if (calcParams.length > 0) create.calcParams = calcParams
  const paneId =
    def.pane === 'main'
      ? chart.createIndicator(create, true, { id: 'candle_pane' })
      : chart.createIndicator(create) // own fresh pane
  if (!paneId) return null

  const live = getIndicator(chart, paneId, engineName)
  // A replay updates the last bar many times a second; recalculate only it (tailIndicatorCalc.ts).
  const tailSafe = def.builtin ? TAIL_SAFE_INDICATORS.has(def.id) : def.tailSafe
  if (live?.calc && tailSafe) {
    const wrapped = def.builtin
      ? withTailCalc(live.calc)
      : withTailCalc(live.calc, { warmup: (cp) => 10 * def.lookback(fromCalcParams(def, cp)) })
    chart.overrideIndicator({ name: engineName, calc: wrapped }, paneId)
  }
  const inputs = def.builtin && Object.keys(s.inputs).length === 0 ? fromCalcParams(def, live?.calcParams) : s.inputs
  const seeded: IndicatorSettings = { ...s, inputs }

  styleIndicator(chart, def, engineName, paneId, seeded)

  const bandIds = (def.levels ?? [])
    .map((lv) =>
      asOverlayId(
        chart.createOverlay(
          {
            name: 'horizontalStraightLine',
            points: [{ value: lv.value }],
            lock: true,
            styles: { line: { style: lv.style === 'solid' ? LineType.Solid : LineType.Dashed, color: lv.color ?? BAND_COLOR, size: 1 } },
            onRightClick: () => true,
          },
          paneId,
        ),
      ),
    )
    .filter((id): id is string => id != null)
  return { paneId, bandIds, settings: seeded }
}

/** Push settings changes to a live indicator (used by the settings dialog). */
export function updateIndicatorStyles(
  chart: Chart,
  def: IndicatorDef,
  inst: { engineName: string; paneId: string; settings: IndicatorSettings },
): void {
  const calcParams = toCalcParams(def, inst.settings.inputs)
  chart.overrideIndicator(
    {
      name: inst.engineName,
      // A builtin with nothing seeded yet keeps the engine's defaults (an empty array would regenerate zero figures).
      ...(calcParams.length > 0 || !def.builtin ? { calcParams } : {}),
      visible: inst.settings.visible !== false,
    },
    inst.paneId,
  )
  styleIndicator(chart, def, inst.engineName, inst.paneId, inst.settings)
}
